import { ChangeEvent, useMemo } from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons/faTimesCircle";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons/faQuestionCircle";
import { POLICY_CONFIG_DECISION_ATTRIBUTE_FIELD_RESULT_OPTIONS, policyConfigDecisionOperatorsWithNoValue } from "@constants/policy-config-decision-attribute-field-result-options";
import { LayoutTypes } from "@enums/layout-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { PolicyConfigDecisionAttributeFieldResultTypes } from "@enums/policy-config-decision-attribute-field-result-type";
import { SelectOption } from "@idw-types/common";
import { FormattedRpServiceConfigurationPolicyConfigDecisionCondition } from "@idw-types/data/rp-service-configuration";
import { SelectFormGroup } from "../../../select-form-group/select-form-group";
import { LabelValueContainer } from "../../../label-value-container/label-value-container";
import { InputFormGroup } from "../../../input-form-group/input-form-group";
import styles from "./policy-decision-condition-attribute-field-container.less";

type PolicyDecisionConditionAttributeFieldContainerProps = {
    isDisabled: boolean;
    condition: FormattedRpServiceConfigurationPolicyConfigDecisionCondition;
    updateCondition: (condition: FormattedRpServiceConfigurationPolicyConfigDecisionCondition) => void;
    removeCondition: (condition: FormattedRpServiceConfigurationPolicyConfigDecisionCondition) => void;
};

export const PolicyDecisionConditionAttributeFieldContainer = (props: PolicyDecisionConditionAttributeFieldContainerProps) => {
    const { isDisabled, condition, updateCondition, removeCondition } = props;

    const handleResultChange = (newValue: SelectOption) => {

        if (policyConfigDecisionOperatorsWithNoValue.includes(newValue.value as PolicyConfigDecisionAttributeFieldResultTypes)) {
            updateCondition({
                ...condition,
                policyConditionOperator: newValue.value as PolicyConfigDecisionAttributeFieldResultTypes,
                values: null
            });
        }
        else {
            updateCondition({
                ...condition,
                policyConditionOperator: newValue.value as PolicyConfigDecisionAttributeFieldResultTypes
            });
        };
    }

    const handleComparisonValueChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateCondition({
            ...condition,
            values: event.target.value
        });
    };

    const handleRemoveCondition = () => {
        removeCondition(condition);
    };

    const renderTooltip = (props: any) => {
        return (
            <Tooltip {...props}>{condition.attributeField.description}</Tooltip>
        );
    };

    const selectedOption = useMemo(() => {
        return POLICY_CONFIG_DECISION_ATTRIBUTE_FIELD_RESULT_OPTIONS.find(option => option.value === condition.policyConditionOperator);
    }, [condition.policyConditionOperator]);

    return (
        <div className={styles.policyDecisionConditionAttributeFieldContainer}>
            <LabelValueContainer
                controlId={`policy-decision-condition-attribute-field-${condition.attributeField.id}-label-value`}
                className={styles.nameLabel}
                isRequired={false}
                label={condition.attributeField.label}
                labelWidth={LabelWidthTypes.EXTRA_SMALL}
                labelIsBold={false}
                layout={LayoutTypes.ROW}
                value={" "}
                afterValueIcon={faQuestionCircle}
            />
            <OverlayTrigger overlay={renderTooltip}>
                <div className={styles.tooltipIconContainer}><FontAwesomeIcon className={styles.tooltipIcon} icon={faQuestionCircle} /></div>
            </OverlayTrigger>
            <SelectFormGroup
                controlId={`policy-decision-condition-attribute-field-${condition.attributeField.id}-result-select`}
                automationId={`policy-decision-condition-attribute-field-${condition.attributeField.id}-result-select`}
                className={styles.resultSelect}
                ariaLabel={""}
                label={""}
                disabled={isDisabled}
                isClearable={false}
                isRequired={true}
                isSearchable={false}
                labelWidth={LabelWidthTypes.EXTRA_SMALL}
                onChange={handleResultChange}
                options={POLICY_CONFIG_DECISION_ATTRIBUTE_FIELD_RESULT_OPTIONS}
                selectedOption={selectedOption}
            />
            {!policyConfigDecisionOperatorsWithNoValue.includes(selectedOption?.value as PolicyConfigDecisionAttributeFieldResultTypes) && (
                    <InputFormGroup
                        controlId="policy-decision-condition-attribute-field-comparison-input"
                        automationId="policy-decision-condition-attribute-field-comparison-input"
                        className={styles.comparisonInput}
                        label={""}
                        ariaLabel={""}
                        placeholder={"Enter a value"}
                        value={condition.values}
                        disabled={isDisabled}
                        isRequired={true}
                        onChange={handleComparisonValueChange}
                        labelWidth={LabelWidthTypes.EXTRA_SMALL}
                    />)}
            {!isDisabled && (
                <span id="policy-decision-condition-attribute-field-remove-condition" className={styles.removeIconContainer} onClick={handleRemoveCondition}>
                    <FontAwesomeIcon className={styles.removeIcon} icon={faTimesCircle} />
                </span>
            )}
        </div>
    );
};